import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Images, BookImage, Wifi } from 'lucide-react';
import { Event } from '../../types';
import { LiveBadge } from '../UI/LiveBadge';
import { clayStaggerItem } from '../../constants/animations';
import styles from './EventCard.module.css';

interface EventCardProps {
  event: Event;
}

export function EventCard({ event }: EventCardProps) {
  const createdBy = event.createdBy && typeof event.createdBy === 'object'
    ? event.createdBy.name
    : 'Unknown';

  const isEnded = event.status === 'ended';

  const displayDate = isEnded && event.endedAt
    ? new Date(event.endedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : new Date(event.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const photoCount  = event.photoCount  ?? 0;
  const albumCount  = event.albumCount  ?? 0;
  const ftpCount    = event.ftpAccountCount ?? 0;

  return (
    <motion.div variants={clayStaggerItem} className={styles['card-wrapper']}>
      <Link to={`/events/${event._id}`} className={styles['card-link']}>
        <motion.div
          className={styles.card}
          whileTap={{ scale: 0.98 }}
          transition={{ type: 'spring', stiffness: 400, damping: 25 }}
        >
          {/* Photo background */}
          {event.recentPhotoUrl ? (
            <div className={styles['bg-photo-wrap']}>
              <img
                src={event.recentPhotoUrl}
                aria-hidden="true"
                className={styles['bg-photo']}
              />
            </div>
          ) : (
            <div className={styles['bg-placeholder']} />
          )}

          {/* Accent line */}
          <div className={styles['accent-line']} />

          {/* Top badges row */}
          <div className={styles['top-badges']}>
            {event.userRole === 'manager' && (
              <span className={styles['role-badge']}>Manager</span>
            )}
            <div className={styles['top-badges-right']}>
              <LiveBadge active={!isEnded} />
            </div>
          </div>

          {/* Bottom content */}
          <div className={styles['bottom-content']}>
            <h3 className={styles.name}>{event.name}</h3>

            {/* Always rendered (empty when there's no description) so every card's
                bottom panel reserves the same height — see `.description` min-height. */}
            <p className={styles.description}>{event.description}</p>

            {/* Stats chips */}
            <div className={styles['stats-row']}>
              <span className={styles['stat-chip']}>
                <Images size={11} />
                {photoCount.toLocaleString()}
              </span>
              <span className={styles['stat-chip']}>
                <BookImage size={11} />
                {albumCount}
              </span>
              {ftpCount > 0 && (
                <span className={styles['stat-chip']}>
                  <Wifi size={11} />
                  {ftpCount}
                </span>
              )}
            </div>

            {/* Meta footer */}
            <div className={styles.footer}>
              <span className={styles['meta-item']}>{createdBy}</span>
              <span className={styles['meta-sep']}>·</span>
              <span className={styles['meta-item']}>
                {isEnded ? 'Ended ' : ''}{displayDate}
              </span>
            </div>
          </div>
        </motion.div>
      </Link>
    </motion.div>
  );
}
